/*
 * Design: Swiss Precision × 和の余白
 * note最新記事3件をRSSフィードから自動取得して表示
 * Mobile: カード余白・フォントサイズ最適化、横スクロール対応
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, BookOpen, Loader2 } from "lucide-react";

interface NoteArticle {
  title: string;
  url: string;
  date: string;
  excerpt: string;
  thumbnail?: string;
}

// フォールバック用の静的データ
const FALLBACK_ARTICLES: NoteArticle[] = [
  {
    title: "Google Workspace×生成AIが加速させる「現場主導のDX」最前線",
    url: "https://note.com/sotaro_81/n/n8ad87587bf5c",
    date: "2026.02.19",
    excerpt:
      "Google関連ニュースを中心に、大企業から自治体、中小企業の現場まで「AI活用の裾野」が劇的に広がっている動向を解説。",
  },
  {
    title: "今週のDX+Googleニュース総まとめ",
    url: "https://note.com/sotaro_81/n/nc2ebe5ada851",
    date: "2026.02.19",
    excerpt:
      "AOKI×Google Workspace、自治体AI活用、AIの思考パートナー化など今週のDX動向を総括。",
  },
  {
    title: "「情報の消費効率」を劇的に変える、Googleドキュメントの新機能活用術",
    url: "https://note.com/sotaro_81/n/n3d3c4fb4a016",
    date: "2026.02.17",
    excerpt:
      "Googleドキュメントの音声要約機能を活用した現場DX推進の具体的手法を解説。",
  },
];

const NOTE_RSS_URL = "https://note.com/sotaro_81/rss";

function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  } catch {
    return dateStr;
  }
}

function parseRssItems(xmlText: string): NoteArticle[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "text/xml");
  const items = doc.querySelectorAll("item");
  const articles: NoteArticle[] = [];

  items.forEach((item, i) => {
    if (i >= 3) return;

    const title = item.querySelector("title")?.textContent || "";
    const link = item.querySelector("link")?.textContent || "";
    const pubDate = item.querySelector("pubDate")?.textContent || "";
    const description = item.querySelector("description")?.textContent || "";
    const thumbnail = item.querySelector("thumbnail")?.getAttribute("url") || 
                      item.getElementsByTagNameNS("http://search.yahoo.com/mrss/", "thumbnail")[0]?.getAttribute("url") || "";

    const excerpt = stripHtml(description).replace(/続きをみる$/, "").trim();

    articles.push({
      title,
      url: link,
      date: formatDate(pubDate),
      excerpt: excerpt.length > 120 ? excerpt.slice(0, 120) + "…" : excerpt,
      thumbnail: thumbnail || undefined,
    });
  });

  return articles;
}

async function fetchNoteArticles(): Promise<NoteArticle[]> {
  const proxyUrls = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(NOTE_RSS_URL)}`,
    `https://corsproxy.io/?${encodeURIComponent(NOTE_RSS_URL)}`,
  ];

  for (const proxyUrl of proxyUrls) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(proxyUrl, { 
        signal: controller.signal,
        headers: { 'Accept': 'application/rss+xml, application/xml, text/xml' }
      });
      clearTimeout(timeoutId);
      if (!res.ok) continue;
      const text = await res.text();
      const articles = parseRssItems(text);
      if (articles.length > 0) return articles;
    } catch {
      continue;
    }
  }

  return FALLBACK_ARTICLES;
}

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function NoteArticlesSection() {
  const [articles, setArticles] = useState<NoteArticle[]>(FALLBACK_ARTICLES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchNoteArticles().then((data) => {
      if (!cancelled) {
        setArticles(data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="py-10 sm:py-16 lg:py-20 bg-[#F9F9F9]">
      <div className="container">
        {/* Compact Header */}
        <motion.div
          {...fadeInUp}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8"
        >
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <BookOpen size={14} className="text-[#E03030] sm:hidden" />
              <BookOpen size={16} className="text-[#E03030] hidden sm:block" />
              <span
                className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                DX Insights
              </span>
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#1A1A1A]">
              DXのヒント — noteで公開中
            </h3>
          </div>
          <a
            href="https://note.com/sotaro_81"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-[#E03030] hover:underline shrink-0"
          >
            すべての記事を見る
            <ExternalLink size={14} />
          </a>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-6 sm:py-8">
            <Loader2 size={20} className="animate-spin text-[#666666]" />
          </div>
        )}

        {/* Article Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {articles.map((article, i) => (
            <motion.a
              key={article.url}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              {...fadeInUp}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group block bg-white border border-[#E0E0E0] overflow-hidden hover:border-[#E03030] transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] active:scale-[0.99] touch-manipulation"
            >
              {/* Thumbnail */}
              {article.thumbnail && (
                <div className="aspect-[16/9] overflow-hidden bg-[#F0F0F0]">
                  <img
                    src={article.thumbnail}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="p-4 sm:p-6">
                {/* Date */}
                <span
                  className="text-[10px] sm:text-[11px] font-medium text-[#666666] mb-2 sm:mb-3 block"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {article.date}
                </span>

                {/* Title */}
                <h4 className="text-xs sm:text-sm font-bold text-[#1A1A1A] mb-2 sm:mb-3 leading-snug group-hover:text-[#E03030] transition-colors line-clamp-2">
                  {article.title}
                </h4>

                {/* Excerpt */}
                <p className="text-[11px] sm:text-xs text-[#333333] leading-relaxed line-clamp-3 mb-3 sm:mb-4">
                  {article.excerpt}
                </p>

                {/* Read more - always visible on mobile */}
                <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-[#E03030] sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                  記事を読む
                  <ExternalLink size={12} />
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
